// src/components/packages/PackagesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetPackages from "../../hooks/packages/useGetPackages";
import useDeletePackage from "../../hooks/packages/useDeletePackage";
import useDeleteAllPackages from "../../hooks/packages/useDeleteAllPackages";
import useRestorePackage from "../../hooks/packages/useRestorePackage";
import useRestoreAllPackages from "../../hooks/packages/useRestoreAllPackages";
import useForceDeletePackage from "../../hooks/packages/useForceDeletePackage";
import useForceDeleteAllPackages from "../../hooks/packages/useForceDeleteAllPackages";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import PackagesTableRow from "./Table/PackagesTableRow";
import PackagesTableColumns from "./Table/PackagesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const PackagesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("packages_list"),
            create: has("packages_create"),
            edit: has("packages_edit"),
            delete: has("packages_delete"),
            forceDelete: has("packages_force_delete"),
            archiveList: has("packages_archive_list"),
            restore: has("packages_restore"),
            restoreAll: has("packages_restore-all-table"),
            deleteAll: has("packages_delete-all-table"),
            massForceDeleteAll: has("packages_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/packages/archive");

    // CRUD Hooks
    const { deletePkg, isDelete } = useDeletePackage();
    const { deleteAllPkg, isDeleteAll } = useDeleteAllPackages();
    const { restorePkg, isRestore } = useRestorePackage();
    const { restoreAllPkg, isRestoreAll } = useRestoreAllPackages();
    const { forceDeletePkg, isForceDelete } = useForceDeletePackage();
    const { forceDeleteAllPkg, isForceDeleteAll } = useForceDeleteAllPackages();

    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/packages",
        deleteSingle: deletePkg,
        deleteAll: deleteAllPkg,
        restoreSingle: restorePkg,
        restoreAll: restoreAllPkg,
        forceDeleteSingle: forceDeletePkg,
        forceDeleteAll: forceDeleteAllPkg,
        isArchive,
    });

    // Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, packages, meta } = useGetPackages(page, perPage, filters);

    const items = useMemo(() => packages?.items || [], [packages]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <PackagesTableRow
            key={params.item.id}
            package={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "خيارات الجولات" : "Tour Options";
    const subtitle = isArchive
        ? isArabic
            ? "عرض خيارات الجولات المحذوفة (الأرشيف)"
            : "Archived Tour Options (soft deleted)"
        : isArabic
            ? "إدارة خيارات الجولات المتاحة في النظام"
            : "Manage available Tour Options in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة خيارات رحلة جديدة"
            : "Create new Tour Options";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllPkg({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/packages/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/packages/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/packages"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={PackagesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد خيارات جولات في الأرشيف" : "لا توجد خيارات جولات"}
                emptyLabelEn={isArchive ? "No archived Tour Options" : "No Tour Options"}
                loadErrorAr="حدث خطأ أثناء تحميل خيارات الجولات"
                loadErrorEn="Failed to load Tour Options"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default PackagesTable;
